import React, { useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { FormattedMessage } from 'react-intl';
import {
  type NativeScrollEvent,
  type NativeSyntheticEvent,
  Platform,
  ScrollView,
  StyleSheet,
} from 'react-native';
import { useSharedValue } from 'react-native-reanimated';
import { CustomNavigationHeader, theme, TitleText, VStack } from '@sg/garnish';
import type {
  DeliveryOrderDetail,
  DeliveryOrderDetailInput,
  Product,
} from '@sg/graphql-schema';

import { Bag } from '@order/features/ordering';
import { useTelemetry, useTrackEffect } from '@order/Telemetry';
import { useA11ySelector } from '@order/utils';

import { UpsellCategory } from './components';

// ─────────────────────────────────────────────────────────────────────────────

export const CategoryUpsells = (props: CategoryUpsellsProps) => {
  const {
    isFetchingCart = false,
    upsellIdsBeingAdded,
    categories,
    deliveryOrderDetail,
    addUpsellToBag,
    onClose,
    onContinue,
  } = props;

  const { formatMessage } = useIntl();
  const { track } = useTelemetry();
  const containerA11ySelector = useA11ySelector(
    'bag.upsell-products.container',
  );

  useTrackEffect('category_upsells.viewed');

  const handleOnContinue = useCallback(() => {
    track('category_upsells.continue.pressed');
    onContinue();
  }, [onContinue, track]);

  const handleOnClose = useCallback(() => {
    track('category_upsells.close.pressed');
    onClose();
  }, [onClose, track]);

  // ─── Scrolling ───────────────────────────────────────────────────────

  const scrollOffsetSV = useSharedValue(0);
  const handleOnScroll = useCallback(
    (event: NativeSyntheticEvent<NativeScrollEvent>) => {
      scrollOffsetSV.value = event.nativeEvent.contentOffset.y;
    },
    [scrollOffsetSV],
  );

  // ─────────────────────────────────────────────────────────────────────

  return (
    <>
      <ScrollView
        stickyHeaderIndices={[0]}
        contentContainerStyle={styles.container}
        scrollEventThrottle={16}
        onScroll={handleOnScroll}
        {...containerA11ySelector.props}
      >
        <CustomNavigationHeader.Container
          palette="cream"
          buttonsReservedSpace="right"
          scrollOffsetSV={scrollOffsetSV}
          safeAreaEdges={safeAreaEdges}
        >
          <CustomNavigationHeader.Content transition={null}>
            <TitleText sizeMatch={['24', '32']}>
              <FormattedMessage {...messages.title} />
            </TitleText>
          </CustomNavigationHeader.Content>

          <CustomNavigationHeader.Button.Right
            name="IconClose"
            accessibilityLabel={formatMessage(messages.closeBag)}
            onPress={handleOnClose}
          />
        </CustomNavigationHeader.Container>

        <VStack style={styles.content}>
          {categories.map((category) => (
            <UpsellCategory
              key={category.id}
              name={category.name}
              products={category.products}
              deliveryOrderDetail={deliveryOrderDetail}
              upsellIdsBeingAdded={upsellIdsBeingAdded}
              addUpsellToBag={addUpsellToBag}
            />
          ))}
        </VStack>
      </ScrollView>

      <Bag.FloatingCtas>
        <Bag.ContinueToBagCta
          isDisabled={isFetchingCart}
          onPress={handleOnContinue}
        />
      </Bag.FloatingCtas>
    </>
  );
};

// ─── Constants ──────────────────────────────────────────────────────────────

const safeAreaEdges = Platform.select({
  android: ['top'] as const,
  default: [],
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    flexGrow: 1,
  },
  content: {
    paddingHorizontal: theme.spacing['4'],
    paddingBottom: theme.spacing['30'],
    marginTop: theme.spacing['6'],
    gap: theme.spacing['12'],
  },
});

// ─── Messages ───────────────────────────────────────────────────────────────

const messages = defineMessages({
  title: {
    defaultMessage: 'Anything else?',
    description: 'Category Upsells | Title',
  },
  dismissUpsells: {
    defaultMessage: 'Dismiss upsells',
    description: 'Category Upsells | Dismiss upsells',
  },
  closeBag: {
    defaultMessage: 'Close Bag',
    description: 'Category Upsells | Close bag button',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type CategoryUpsellsProps = {
  categories: UpsellCategories;
  isFetchingCart?: boolean;
  upsellIdsBeingAdded: string[];
  deliveryOrderDetail?: DeliveryOrderDetail;
  addUpsellToBag: (payload: {
    product: Partial<Product>;
    deliveryDetails?: DeliveryOrderDetailInput;
  }) => void;
  onClose: () => void;
  onContinue: () => void;
};

type UpsellCategories = {
  id: string;
  name: string;
  categoryRanking: number;
  products: UpsellProduct[];
}[];

type UpsellProduct = Readonly<{
  id: string;
  name: string;
  cost: number;
  imageUrl: string;
  outOfStock: boolean;
  menuId: number;
}>;
